import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { CircularIconButton } from "~/components/CircularIconButton/CircularIconButton";
import { IconButtonType } from "~/components/CircularIconButton/DynamicButtonIcon";
import { ConfirmationDialog } from "../ConfirmationDialog";
import {
  GetActivePoliciesDocument,
  useDeleteCustomPolicyMutation,
  useDeleteCustomQueryPackMutation,
  useUnassignPolicyMutation,
} from "~/operations";
import { useCanDeletePolicy } from "~/lib/iam/actions/cnspec/policy/PolicyHub/DeletePolicy";
import { Scope, ScopeType } from "~/hooks/useScope";

type IsQueryPack = {
  policyMrn?: never;
  queryPackMrn: string;
};

type IsPolicy = {
  policyMrn: string;
  queryPackMrn?: never;
};

type PolicyDeleteButtonProps = {
  space: Scope;
  isCustom?: boolean;
} & (IsPolicy | IsQueryPack);

// This component is used for deleting policies OR query packs from a space
export function PolicyDeleteButton({
  space,
  policyMrn,
  queryPackMrn,
  isCustom,
}: PolicyDeleteButtonProps) {
  const scope = space;
  let navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const canDeletePolicy = useCanDeletePolicy(scope);

  // Deactivate a policy or query pack from a single space
  const [disablePolicy, disablePolicyReturn] = useUnassignPolicyMutation({
    variables: {
      input: {
        assetMrn: scope.mrn,
        policyMrn: policyMrn || queryPackMrn,
      },
    },
    refetchQueries: [GetActivePoliciesDocument],
  });

  // Delete a custom policy from a single space.  Policy must be deactivated first.
  const [deleteCustomPolicy, deleteCustomPolicyReturn] =
    useDeleteCustomPolicyMutation({
      variables: {
        input: {
          policyMrn: policyMrn || "",
        },
      },
    });

  const [deleteCustomQueryPack, deleteCustomQueryPackReturn] =
    useDeleteCustomQueryPackMutation({
      variables: {
        input: {
          queryPackMrn: queryPackMrn || "",
        },
      },
    });

  // When a user clicks the confirm button, the policy will be disabled and if custom, deleted
  const handleConfirm = async () => {
    try {
      await disablePolicy();
      // custom policies and query packs must be disabled before they can be deleted
      if (isCustom && policyMrn) {
        if (canDeletePolicy) {
          await deleteCustomPolicy();
        } else {
          throw new Error("User does not have permission to delete Policies");
        }
      }
      if (isCustom && queryPackMrn) {
        if (canDeletePolicy) {
          await deleteCustomQueryPack();
        } else {
          throw new Error(
            "User does not have permission to delete Query Packs",
          );
        }
      }
      setModalOpen(false);
      let url = "";
      if (scope.type === ScopeType.Space) {
        const base = policyMrn
          ? "/space/findings/policies"
          : "/space/inventory/query-packs";
        url = `${base}?${scope.params}`;
      }
      if (scope.type === ScopeType.Organization) {
        const base = policyMrn
          ? "/organization/policies"
          : "/organization/query-packs";
        url = `${base}?${scope.params}`;
      }
      if (scope.type === ScopeType.Region) {
        url = policyMrn ? "/policies" : "/query-packs";
      }
      navigate(url);
    } catch (error) {
      console.error(error);
    }
  };

  const generateTitle = () => {
    if (isCustom && policyMrn) {
      return "DELETE THIS CUSTOM POLICY FROM THE SPACE?";
    }

    if (isCustom && queryPackMrn) {
      return "DELETE THIS CUSTOM QUERY PACK FROM THE SPACE?";
    }

    if (policyMrn) {
      return "REMOVE THIS POLICY FROM THE SPACE?";
    }

    if (queryPackMrn) {
      return "REMOVE THIS QUERY PACK FROM THE SPACE?";
    }

    return "REMOVE THIS FROM THE SPACE?";
  };

  const generateContentText = () => {
    if (isCustom && policyMrn) {
      return "Uploaded policies will be deleted permanently, alongside all data connected to them. You can only restore them by re-uploading them, so make sure to download a backup first.";
    }

    if (isCustom && queryPackMrn) {
      return "Uploaded query packs will be deleted permanently, alongside all data connected to them. You can only restore them by re-uploading them, so make sure to download a backup first.";
    }

    return "You can add and run this policy again any time, but any data collected or stored in connection to it will be deleted, and cannot be restored.";
  };

  const loading =
    disablePolicyReturn.loading ||
    deleteCustomPolicyReturn.loading ||
    deleteCustomQueryPackReturn.loading;

  return (
    <Fragment>
      <CircularIconButton
        title="Remove"
        iconType={IconButtonType.Delete}
        sx={{ color: (theme) => theme.palette.error.main }}
        onClick={() => setModalOpen(true)}
      />
      <ConfirmationDialog
        isOpen={modalOpen}
        loading={loading}
        onConfirm={() => handleConfirm()}
        onClose={() => setModalOpen(false)}
        title={generateTitle()}
        content={
          <Typography variant="body2">{generateContentText()}</Typography>
        }
      />
    </Fragment>
  );
}
